import { CheckBoxWithLabelProps } from '@tableau/widgets';
import * as _ from 'underscore';
import { Messages } from '../../codegen/Localize';
import { ISiteRolePickerMenuScope, SiteRolePickerMenuHelper } from '../helpers/SiteRolePickerMenuHelper';
import { IGroupBasicInfo } from '../models/IGroupInfo';
import { ISliceable } from '../models/ISliceable';
import { Set } from '../models/Set';
import { Module } from '../Module';
import { ActiveDirectoryGroups } from '../resources/ActiveDirectoryGroups';
import { DefaultSiteRole, SiteRoleOption, SiteRoleOptionsResource } from '../resources/SiteRoleOptions';
import { SiteRolesResource } from '../resources/SiteRoles';
import { ISearchActiveDirectoryGroupNameResult } from '../ServerApi/requests/SearchActiveDirectoryGroupNameRequest';
import { GrantLicenseModes } from '../ServerApi/requests/SyncActiveDirectoryGroupRequest';
import { IGroup } from '../ServerApi/types/IGroup';
import { SiteRoles } from '../ServerApi/types/ISiteRole';
import { IAddAdGroupDialogScope } from '../services/ActiveDirectoryGroupAction';
import { ServerService } from '../services/ServerService';
import { SliceableFactory } from '../services/SliceableFactory';

export class AddActiveDirectoryGroupDialogCtrl {

  public static $inject = [
    '$scope',
    '$timeout',
    '$translate',
    'ActiveDirectoryGroups',
    'ServerService',
    'SiteRoles',
    'SiteRoleOptionsResource',
    'SliceableFactory'
  ];

  public static INPUT_DEBOUNCE_MS: number = 200;
  public static SEARCH_TERM_TIMEOUT_MS: number = 4000;

  private searchOverridden: boolean = false;
  private searchTimeout: ng.IPromise<any> = null;

  constructor(
    private $scope: AddActiveDirectoryGroupDialogCtrl.Scope,
    private $timeout: ng.ITimeoutService,
    private $translate: ng.translate.ITranslateService,
    private activeDirectoryGroups: ActiveDirectoryGroups,
    private serverService: ServerService,
    private siteRoles: SiteRolesResource,
    private siteRoleOptionsResource: SiteRoleOptionsResource,
    private sliceableFactory: SliceableFactory
  ) {
    // initialize search stuff
    $scope.groupSearchValue = { text: '' };
    $scope.isSearchStringEmpty = () => ($scope.groupSearchValue.text !== '');

    const debouncedUpdate = _.debounce((newValue: string) => {
      this.$scope.$evalAsync(() => {
        this.onSearchTextChanged(newValue);
      });
    }, AddActiveDirectoryGroupDialogCtrl.INPUT_DEBOUNCE_MS);

    $scope.$watch('groupSearchValue.text', (newSearchText: string) => {
      this.resetSelection();
      this.$scope.searchTimeoutExceeded = false;
      this.$scope.sliceable = this.sliceableFactory.fromArray();
      if (_.isString(newSearchText) && newSearchText.length > 0) {
        // input is a non-empty string
        this.$scope.hasResults = false;
        this.$scope.statusMessage = this.$translate.instant('addUsersImportAdGroupSearching');
        debouncedUpdate(newSearchText);
      } else {
        // input is empty
        this.$scope.hasResults = false;
        $scope.statusMessage = '';
      }
    });

    // initialize state
    $scope.hasResults = false;
    $scope.searchTimeoutExceeded = false;
    $scope.statusMessage = '';
    $scope.sliceable      = this.sliceableFactory.fromArray(); // list of groups, initially empty.
    $scope.selectedItems  = new Set<IGroup>((group: IGroup) => group.id);
    $scope.disabledItems  = new Set<IGroup>((group: IGroup) => group.id);
    $scope.groupsToImport = new Set<IGroup>((group: IGroup) => group.id);

    $scope.siteRoleOptions = this.siteRoles.optionsForCurrentUserExcept(SiteRoles.ServerAdministrator);

    const onSiteRoleSelected = (roleOption: SiteRoleOption) => {
      this.$scope.$apply(() => {
        $scope.siteRoleSelectorProps.selectedRole = roleOption;
        $scope.group.siteRole = roleOption.role;
        let isUnlicensedSiteRole = $scope.group.siteRole === SiteRoles.Unlicensed;
        $scope.group.grantLicenseMode =
          isUnlicensedSiteRole ? GrantLicenseModes.ON_SYNC : $scope.group.grantLicenseMode;
        $scope.licenseUponLoginCheckboxProps =
          {...$scope.licenseUponLoginCheckboxProps,
              disabled: isUnlicensedSiteRole,
              checked: $scope.group.grantLicenseMode === GrantLicenseModes.ON_LOGIN
          };
      });
    };

    SiteRolePickerMenuHelper.setupPickerMenuScope($scope, $translate, serverService, siteRoleOptionsResource, DefaultSiteRole, onSiteRoleSelected, true);

    // link some private functions to this scope
    $scope.rowClicked = (row: IGroup, colIndex: number) => {
      this.onRowClicked(row);
    };

    $scope.isRowSelected = (locals: { row: IGroup }) => {
      return $scope.groupsToImport.contains(locals.row);
    };

    $scope.useSearchTerm = () => this.useSearchTerm();

    $scope.group.grantLicenseMode = GrantLicenseModes.ON_SYNC;
    const toggleLicenseUponLogin = (checked: boolean) => {
      $scope.group.grantLicenseMode =
        checked ? GrantLicenseModes.ON_LOGIN : GrantLicenseModes.ON_SYNC;
      $scope.licenseUponLoginCheckboxProps =
        {...$scope.licenseUponLoginCheckboxProps,
            checked: checked
        };
      $scope.$digest();
    };

    $scope.licenseUponLoginCheckboxProps = {
      checked: false,
      handleChange: toggleLicenseUponLogin.bind(this),
      containerStyle: {marginLeft: '0px', marginTop: '16px'},
      label: Messages.grantLicenseUponLoginCheckboxLabel(),
      testId: 'license-upon-login'
    };
  }

  // whenever a row is clicked, add it to the list of groups to import & selected items.
  private onRowClicked(row: IGroup) {
    if (!this.$scope.disabledItems.contains(row)) {
      // row is enabled.
      if (!this.$scope.selectedItems.contains(row)) {
        // the clicked row isn't selected.  reset selection before selecting it (to force single-select).
        this.resetSelection();
      }
      this.$scope.selectedItems.toggle(row);
      this.$scope.groupsToImport.toggle(row);

      this.$scope.group.model = {...row, contentType: 'group'} as IGroupBasicInfo;
    }
  }

  // whenever a user searches, fetch the groups and set the sliceable.
  private onSearchTextChanged(newSearchText: string) {
    if (_.isString(newSearchText)
      && newSearchText.length > 0) {

      const searchInput = AddActiveDirectoryGroupDialogCtrl.prepareSearchTextInput(newSearchText);
      this.searchOverridden = false;

      this.activeDirectoryGroups.searchActiveDirectoryGroupName(searchInput).then((result: ISearchActiveDirectoryGroupNameResult) => {
        if (newSearchText === this.$scope.groupSearchValue.text && !this.searchOverridden) {
          this.stopSearchTimeout();

          this.$scope.sliceable = this.sliceableFactory.fromArray(result.groups);
          // checking newSearchText against the groupSearchValue is to make sure this result isn't stale.
          // if the result is stale now, skip all this because we know a later result will be handled.
          // TODO: how should we handle LDAP errors when searching?
          if (result.hasMoreItems) {
            // received a partial list of the search results (truncated by server).
            this.$scope.hasResults = true;
            this.$scope.statusMessage = this.$translate.instant('addUsersImportAdGroupLimit');
          } else if (!result.groups || result.groups.length === 0) {
            // received no results.
            this.$scope.hasResults = false;
            this.$scope.statusMessage = this.$translate.instant('addUsersImportAdGroupNoResults');
          } else {
            // default case: received at least one result, not truncated.
            this.$scope.hasResults = true;
            this.$scope.statusMessage = '';
          }
        }
      });

      this.startSearchTimeout();
    } else {
      // invalid search, ignore.
    }
  }

  private stopSearchTimeout() {
    this.$scope.searchTimeoutExceeded = false;
    this.$timeout.cancel(this.searchTimeout);
    this.searchTimeout = null;
  }

  private startSearchTimeout() {
    // Cancel any existing timer
    if (this.searchTimeout) {
      this.$timeout.cancel(this.searchTimeout);
    }
    // Start new timer
    this.searchTimeout = this.$timeout(() => this.handleTimeoutExceeded(),
      AddActiveDirectoryGroupDialogCtrl.SEARCH_TERM_TIMEOUT_MS);
    this.$scope.searchTimeoutExceeded = false;
  }

  private handleTimeoutExceeded() {
    // Clear timer variable
    this.searchTimeout = null;
    this.$scope.searchTimeoutExceeded = true;
  }

  private useSearchTerm() {
    this.searchOverridden = true;
    this.$scope.hasResults = true;
    this.$scope.searchTimeoutExceeded = false;
    this.$scope.statusMessage = '';

    const searchTermGroup: IGroup = {
      id: null,
      domainName: this.serverService.defaultDomain(),
      name: this.$scope.groupSearchValue.text,
      isAllUsersGroup: null
    };
    const groupNameBackslashIndex = searchTermGroup.name.indexOf('\\');
    if (groupNameBackslashIndex > 0) {
      searchTermGroup.domainName = searchTermGroup.name.substr(0, groupNameBackslashIndex);
      searchTermGroup.name = searchTermGroup.name.substr(groupNameBackslashIndex + 1);
    }

    this.$scope.sliceable = this.sliceableFactory.fromArray([searchTermGroup]);
    this.onRowClicked(searchTermGroup);
  }

  // helpers

  private resetSelection() {
    this.$scope.selectedItems.clear();
    this.$scope.groupsToImport.clear();
    this.$scope.group.model = null;

    // reset the dropdown props and scope.group.siteRole to default site role
    this.$scope.group.siteRole = DefaultSiteRole;
    this.$scope.siteRoleSelectorProps.selectedRole = this.siteRoleOptionsResource.getSiteRoleOptionBySiteRole(DefaultSiteRole);
  }

  private static prepareSearchTextInput(searchText: string): string {
    // regex for exact search terms: begins & ends with ", with at least one char between.
    const exactSearchTermRegexp: RegExp = /^["].+["]$/;
    // regex for wildcard search terms: anything containing an asterisk.
    const wildSearchTermRegexp: RegExp = /.*[*]+.*/;

    // change the search text if needed, in preparation for searchActiveDirectoryGroupName.
    let result: string;
    if (exactSearchTermRegexp.test(searchText)) {
      // exact search: return the string with the " marks removed from the ends.
      result = searchText.substring(1, searchText.length-1);
    } else if (wildSearchTermRegexp.test(searchText)) {
      // wildcard search: don't change the string.
      result = searchText;
    } else {
      // fuzzy search: return the string with * prepended and appended around the group name.
      if (searchText.search(/\\/) > 0) {
        // input includes a backslash, which indicates domainName\groupName format;
        // append & prepend * to the input after the slash.
        result = searchText.replace(/([^\\]+)\\([^\\]+)/g, '$1\\*$2*');
      } else { // no backslash in input
        // append & prepend * to the text input.
        result = searchText.replace(/(.+)/g, '*$1*');
      }
    }
    return result;
  }

}

export module AddActiveDirectoryGroupDialogCtrl {

  export interface GroupSearchValue {
    text: string;
  }

  export interface Scope extends IAddAdGroupDialogScope, ISiteRolePickerMenuScope {
    // for searching the groups
    isSearchStringEmpty(): boolean;
    groupSearchValue: GroupSearchValue;

    // for displaying/selecting from the list of groups
    isRowSelected(locals: { row: IGroup }): boolean;
    rowClicked(row: IGroup, colIndex: number): void;
    disabledItems: Set<IGroup>;     // for grid state
    useSearchTerm(): void;
    groupsToImport: Set<IGroup>;    // groups to add
    selectedItems: Set<IGroup>;     // for grid state
    sliceable: ISliceable<IGroup>;

    // for the license upon login checkbox
    licenseUponLoginCheckboxProps: CheckBoxWithLabelProps;

    // for the site role selector
    setSiteRole(role: string): void;
    siteRoleOptions: string[];

    // for the dialog/modal
    hasResults: boolean;
    searchTimeoutExceeded: boolean;
    statusMessage: string;
  }

}

Module.instance().controller('AddActiveDirectoryGroupDialogCtrl', AddActiveDirectoryGroupDialogCtrl);
